import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';

export default function Companies() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    phone: '', 
    gst_number: '',
    auto_send_enabled: false 
  });
  const [editId, setEditId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/companies');
      setCompanies(Array.isArray(response.data) ? response.data : response.data.companies || []);
      setError('');
    } catch (err) {
      setError('Failed to fetch companies');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editId) {
        await api.patch(`/companies/${editId}`, formData);
      } else {
        await api.post('/companies', formData);
      }
      setFormData({ name: '', email: '', phone: '', gst_number: '', auto_send_enabled: false });
      setShowForm(false);
      setEditId(null);
      fetchCompanies();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save company');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (company) => {
    setFormData({
      name: company.name,
      email: company.email || '',
      phone: company.phone || '',
      gst_number: company.gst_number || '',
      auto_send_enabled: company.auto_send_enabled || false
    });
    setEditId(company.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this company?')) {
      try {
        await api.delete(`/companies/${id}`);
        fetchCompanies();
      } catch (err) {
        setError('Failed to delete company');
      }
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>Companies</h2>
        <button 
          onClick={() => { 
            setShowForm(!showForm); 
            if (showForm) { 
              setEditId(null); 
              setFormData({ name: '', email: '', phone: '', gst_number: '', auto_send_enabled: false }); 
            } 
          }} 
          className="btn-primary"
          style={{ padding: '10px 20px', backgroundColor: '#0066cc', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
        >
          {showForm ? 'Cancel' : '+ Add Company'}
        </button>
      </div>

      {error && <div className="alert alert-error" style={{ color: '#c00', padding: '10px', backgroundColor: '#fee', borderRadius: '4px', marginBottom: '20px' }}>{error}</div>}

      {showForm && (
        <form onSubmit={handleSubmit} className="company-form" style={{ padding: '20px', border: '1px solid #ddd', borderRadius: '4px', marginBottom: '20px', backgroundColor: '#f9f9f9' }}>
          <h3>{editId ? 'Edit Company' : 'Add New Company'}</h3>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Company Name *</label>
            <input 
              type="text" 
              value={formData.name} 
              onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
              required 
              style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Email</label>
              <input 
                type="email" 
                value={formData.email} 
                onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Phone</label>
              <input 
                type="tel" 
                value={formData.phone} 
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })} 
                style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>GST Number</label>
            <input 
              type="text" 
              value={formData.gst_number} 
              onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })} 
              style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input 
              type="checkbox" 
              checked={formData.auto_send_enabled} 
              onChange={(e) => setFormData({ ...formData, auto_send_enabled: e.target.checked })}
              style={{ cursor: 'pointer', width: '18px', height: '18px' }}
            />
            <label style={{ cursor: 'pointer', margin: 0 }}>
              <strong>Enable Auto-Send WhatsApp Reminders</strong>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '3px' }}>Daily compliance reminders at 8 AM IST</div>
            </label>
          </div>

          <button 
            type="submit" 
            disabled={submitting}
            className="btn-primary"
            style={{ padding: '10px 20px', backgroundColor: '#0066cc', color: 'white', border: 'none', borderRadius: '4px', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.6 : 1 }}
          >
            {submitting ? 'Saving...' : editId ? 'Update' : 'Add'} Company
          </button>
        </form>
      )}

      {loading ? <p style={{ textAlign: 'center', color: '#666' }}>Loading...</p> : companies.length === 0 ? <p style={{ textAlign: 'center', color: '#999' }}>No companies yet.</p> : (
        <div className="companies-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
          {companies.map((company) => (
            <div key={company.id} className="company-card" style={{ border: '1px solid #ddd', padding: '15px', borderRadius: '4px', backgroundColor: 'white' }}>
              <div className="company-info">
                <h3>{company.name}</h3>
                <p><strong>Email:</strong> {company.email || 'N/A'}</p>
                <p><strong>Phone:</strong> {company.phone || 'N/A'}</p>
                <p><strong>GST:</strong> {company.gst_number || 'N/A'}</p>
                <p style={{ marginTop: '10px' }}>
                  <strong>Auto-Send:</strong> 
                  <span style={{ 
                    marginLeft: '8px',
                    padding: '4px 8px', 
                    backgroundColor: company.auto_send_enabled ? '#e8f5e9' : '#ffebee',
                    color: company.auto_send_enabled ? '#2e7d32' : '#c62828',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}>
                    {company.auto_send_enabled ? '✅ Enabled' : '❌ Disabled'}
                  </span>
                </p>
              </div>
              <div className="company-actions" style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                <button 
                  onClick={() => handleEdit(company)} 
                  className="btn-edit"
                  style={{ padding: '8px 15px', backgroundColor: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', flex: 1 }}
                >
                  Edit
                </button>
                <button 
                  onClick={() => handleDelete(company.id)} 
                  className="btn-delete"
                  style={{ padding: '8px 15px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', flex: 1 }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
